/**
 * Human-readable text for HTTP 429 (FEAT-047 TASK-023, design.md section 5).
 *
 * The API client turns every 429 into an ApiClientError whose message is built here, so no
 * screen ever shows the limiter's raw text ("Too many requests. Try again in 300 seconds.").
 * The wait time comes only from the Retry-After header (seconds or HTTP date): the 429 envelope
 * carries no seconds field, and numbers are never extracted from the message text.
 * Rounding is always up, so the user is never told to retry too early.
 */

import { createIntl, createIntlCache, type IntlShape } from 'react-intl';
import ptBR from '@/i18n/messages/pt-BR.json';
import enUS from '@/i18n/messages/en-US.json';
import type { Messages, SupportedLocale } from '@/i18n/types';
import { getQuotaExceededInfo } from '@/utils/apiErrors';
import { formatQuotaExceededText } from '@/utils/quotaExceededText';

const MESSAGE_BUNDLES: Record<SupportedLocale, Messages> = {
  'pt-BR': ptBR,
  'en-US': enUS,
};
const LOCALE_STORAGE_KEY = 'opus-ia-lang';
const DEFAULT_LOCALE: SupportedLocale = 'pt-BR';

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;

const intlCache = createIntlCache();
const intlByLocale = new Map<SupportedLocale, IntlShape>();

function isSupportedLocale(value: string | null | undefined): value is SupportedLocale {
  return value === 'pt-BR' || value === 'en-US';
}

/** Active UI locale outside React: the <html lang> the IntlProvider keeps in sync, then storage. */
function getActiveLocale(): SupportedLocale {
  if (typeof document !== 'undefined' && isSupportedLocale(document.documentElement.lang)) {
    return document.documentElement.lang;
  }
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isSupportedLocale(stored)) return stored;
  } catch {
    // storage blocked: fall through to the default
  }
  return DEFAULT_LOCALE;
}

function getIntl(locale: SupportedLocale): IntlShape {
  let intl = intlByLocale.get(locale);
  if (!intl) {
    intl = createIntl(
      { locale, defaultLocale: DEFAULT_LOCALE, messages: MESSAGE_BUNDLES[locale], onError: () => {} },
      intlCache,
    );
    intlByLocale.set(locale, intl);
  }
  return intl;
}

/**
 * Seconds to wait from a Retry-After header value (delta-seconds or HTTP date).
 * Returns null when absent, zero, negative or unreadable.
 */
export function parseRetryAfterSeconds(
  header: string | null | undefined,
  nowMs: number = Date.now(),
): number | null {
  const value = header?.trim();
  if (!value) return null;

  const seconds = /^\d+(\.\d+)?$/.test(value) ? Number(value) : (Date.parse(value) - nowMs) / 1000;
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return Math.ceil(seconds);
}

/** "Muitas tentativas. Tente de novo em 5 minutos." (never the limiter's own text). */
export function buildRateLimitMessage(retryAfterSeconds: number | null): string {
  const intl = getIntl(getActiveLocale());
  const title = intl.formatMessage({ id: 'rateLimit.title' });

  let wait: string;
  if (retryAfterSeconds === null || retryAfterSeconds <= 0) {
    wait = intl.formatMessage({ id: 'rateLimit.retryIn.unknown' });
  } else if (retryAfterSeconds < SECONDS_PER_MINUTE) {
    wait = intl.formatMessage({ id: 'rateLimit.retryIn.seconds' }, { seconds: retryAfterSeconds });
  } else if (retryAfterSeconds < SECONDS_PER_HOUR) {
    wait = intl.formatMessage(
      { id: 'rateLimit.retryIn.minutes' },
      { minutes: Math.ceil(retryAfterSeconds / SECONDS_PER_MINUTE) },
    );
  } else {
    wait = intl.formatMessage(
      { id: 'rateLimit.retryIn.hours' },
      { hours: Math.ceil(retryAfterSeconds / SECONDS_PER_HOUR) },
    );
  }

  return `${title}. ${wait}`;
}

/** Fallback for a quota-exhausted 429 whose envelope carries no message. */
export function buildQuotaExceededMessage(): string {
  return getIntl(getActiveLocale()).formatMessage({ id: 'errors.rateLimit.quotaExceeded' });
}

// --- FEAT-054 TASK-011 (cota) ---
/**
 * Message of a 429 quota error from its grace details (mode, percentage, renewal in the org time
 * zone), in the active locale. Null when the details are absent/unreadable or the bundle lacks the
 * text: the caller keeps the server message.
 */
export function buildQuotaExceededFromDetails(status: number, code: unknown, details: unknown): string | null {
  const info = getQuotaExceededInfo(status, typeof code === 'string' ? code : undefined, details);
  return info ? formatQuotaExceededText(info, getIntl(getActiveLocale())) : null;
}
// --- fim FEAT-054 TASK-011 (cota) ---
